import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { consentService, type ConsentSnapshot } from './consent';

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = (Math.random() * 16) | 0;
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

const CLARITY_ID_STORAGE_KEY = 'claroclaim_clarity_id';

class MonitoringService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private initialized = false;
  private analyticsConsent = false;
  private listeners = new Set<(consent: boolean) => void>();
  private connectionString?: string;

  public initialize(connectionString?: string): void {
    if (typeof window === 'undefined' || this.initialized) return;
    this.initialized = true;
    this.connectionString = connectionString || import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING;
    consentService.subscribe((snapshot) => this.handleConsent(snapshot));
    void consentService.initialize();
  }

  public subscribe(callback: (consent: boolean) => void): () => void {
    this.listeners.add(callback);
    callback(this.analyticsConsent);
    return () => this.listeners.delete(callback);
  }

  public getClarityId(): string {
    if (typeof localStorage === 'undefined' || !consentService.canUse('analytics')) return '';
    let clarityId = localStorage.getItem(CLARITY_ID_STORAGE_KEY);
    if (!clarityId) {
      clarityId = generateUUID();
      localStorage.setItem(CLARITY_ID_STORAGE_KEY, clarityId);
    }
    return clarityId;
  }

  private handleConsent(snapshot: ConsentSnapshot): void {
    const allowed = snapshot.ready && snapshot.categories.analytics === true;
    if (allowed === this.analyticsConsent) return;
    this.analyticsConsent = allowed;
    if (allowed) this.enableApplicationInsights();
    else this.disableApplicationInsights();
    for (const listener of this.listeners) listener(allowed);
  }

  private enableApplicationInsights(): void {
    if (!this.reactPlugin) this.reactPlugin = new ReactPlugin();

    if (this.connectionString && !this.appInsights) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: this.connectionString,
          extensions: [this.reactPlugin],
          extensionConfig: { [this.reactPlugin.identifier]: {} },
          disableCookiesUsage: false,
          disableAjaxTracking: false,
          disableFetchTracking: false,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          correlationHeaderExcludedDomains: [
            '*.wistia.com',
            '*.wistia.net',
            'fast.wistia.com',
            'fast.wistia.net',
            'embedwistia-a.akamaihd.net',
          ],
          enableRequestHeaderTracking: false,
          enableResponseHeaderTracking: false,
          disableExceptionTracking: false,
          disableTelemetry: false,
          maxBatchInterval: 15000,
          disableDataLossAnalysis: false,
          isStorageUseDisabled: false,
          isCookieUseDisabled: false,
        },
      });
      this.appInsights.loadAppInsights();
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.tags) {
          delete envelope.tags['ai.user.accountId'];
          delete envelope.tags['ai.user.id'];
          delete envelope.tags['ai.user.authUserId'];
        }
        return consentService.canUse('analytics');
      });
    } else if (this.appInsights) {
      this.appInsights.config.disableCookiesUsage = false;
      this.appInsights.config.disableTelemetry = false;
      this.appInsights.config.isStorageUseDisabled = false;
      this.appInsights.config.isCookieUseDisabled = false;
    }

    const clarityId = this.getClarityId();
    if (clarityId) window.clarity?.('identify', clarityId);
  }

  private disableApplicationInsights(): void {
    if (this.appInsights) {
      this.appInsights.config.disableCookiesUsage = true;
      this.appInsights.config.disableTelemetry = true;
      this.appInsights.config.isStorageUseDisabled = true;
      this.appInsights.config.isCookieUseDisabled = true;
      this.appInsights.clearAuthenticatedUserContext();
    }
    localStorage.removeItem(CLARITY_ID_STORAGE_KEY);
  }

  public trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (!consentService.canUse('analytics') || !this.appInsights) return;
    this.appInsights.trackEvent({ name }, this.sanitizeProperties(properties));
  }

  public trackException(exception: Error, properties?: Record<string, unknown>): void {
    if (!consentService.canUse('analytics') || !this.appInsights) return;
    this.appInsights.trackException({ exception, properties: this.sanitizeProperties(properties) });
  }

  public trackPageView(name?: string, properties?: Record<string, unknown>): void {
    if (!consentService.canUse('analytics')) return;
    this.appInsights?.trackPageView({ name, properties: this.sanitizeProperties(properties) });
    const clarityId = this.getClarityId();
    if (clarityId) window.clarity?.('identify', clarityId);
  }

  public trackClarityEvent(name: string): void {
    if (!consentService.canUse('analytics')) return;
    window.clarity?.('event', name);
  }

  public trackRedditEvent(eventName: string, conversionId?: string): void {
    if (!consentService.canUse('marketing')) return;
    window.rdt?.('track', eventName, { conversionId: conversionId || generateUUID() });
  }

  public identifyRedditUser(email: string): void {
    if (!consentService.canUse('marketing')) return;
    window.rdt?.('init', 'a2_i8go08zmgpth', { email });
  }

  public trackMetaEvent(eventName: string, eventId?: string): void {
    if (!consentService.canUse('marketing')) return;
    window.fbq?.('track', eventName, {}, { eventID: eventId || generateUUID() });
  }

  public identifyMetaUser(email: string): void {
    if (!consentService.canUse('marketing')) return;
    window.fbq?.('init', '1038498498001498', { em: email });
  }

  public trackXEvent(eventName: string, conversionId?: string): void {
    if (!consentService.canUse('marketing')) return;
    window.twq?.('event', eventName, { conversion_id: conversionId || generateUUID() });
  }

  public identifyXUser(email: string): void {
    if (!consentService.canUse('marketing')) return;
    window.twq?.('config', 'qy0lg', { em: email });
  }

  private sanitizeProperties(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!properties) return undefined;
    const sanitized = { ...properties };
    for (const field of ['email', 'name', 'phone', 'address', 'ssn', 'password']) delete sanitized[field];
    return sanitized;
  }

  public getReactPlugin(): ReactPlugin | null {
    return this.reactPlugin;
  }

  public isConsentGiven(): boolean {
    return consentService.canUse('analytics');
  }

  public openConsentSettings(): void {
    consentService.openPreferences();
  }
}

export const monitoringService = new MonitoringService();
